import { c as _c } from "react/compiler-runtime";
// `<OgImage>` — absolute-URL guarded image for OG cards.
//
// Satori (the renderer behind `next/og`) has narrow image rules:
//   - URLs must be absolute (no relative paths, no bare `/foo.png`)
//   - Only PNG and JPEG render reliably; SVG / WebP / AVIF are rejected
//   - A network-fetch failure logs but does not throw
//
// This primitive fails closed. If `src` is missing, relative, or has a
// non-renderable extension, it renders a coloured placeholder div with
// the same dimensions instead of letting Satori swallow the failure
// silently. That keeps OG cards visually intentional even when the
// upstream image is broken.

import type { CSSProperties } from 'react';
import { OG_TOKENS } from '../tokens';
import { isOgRenderableSrc, transformIpfsForSatori } from './og-image-src';
import { registerImageUrl } from './register-image-url';
export interface OgImageProps {
  /** Absolute URL (https://...). Anything else renders the placeholder. */
  src: string | null | undefined;
  /** Alt text. Satori writes it into the rendered SVG `<title>`. */
  alt: string;
  width: number;
  height: number;
  /** Optional rounding. Numbers are in pixels; `9999` makes a circle. */
  borderRadius?: number;
  /** Object-fit. Defaults to `cover`. */
  fit?: 'cover' | 'contain';
  /** Placeholder background when `src` can't be used. Defaults to brand-700. */
  fallbackBackground?: string;
  /** Extra inline styles merged onto the wrapper. */
  style?: CSSProperties;
}
export function OgImage(props) {
  const $ = _c(23);
  const radius = props.borderRadius ?? 0;
  const t0 = props.fallbackBackground ?? OG_TOKENS.brand700;
  let t1;
  if ($[0] !== props.height || $[1] !== props.style || $[2] !== props.width || $[3] !== radius || $[4] !== t0) {
    t1 = {
      display: "flex",
      width: props.width,
      height: props.height,
      borderRadius: radius,
      overflow: "hidden",
      background: t0,
      ...props.style
    };
    $[0] = props.height;
    $[1] = props.style;
    $[2] = props.width;
    $[3] = radius;
    $[4] = t0;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const wrapperStyle = t1;
  if (!isOgRenderableSrc(props.src)) {
    let t2;
    if ($[6] !== wrapperStyle) {
      t2 = <div style={wrapperStyle} />;
      $[6] = wrapperStyle;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  let resolvedSrc;
  if ($[8] !== props.src) {
    resolvedSrc = transformIpfsForSatori(props.src);
    registerImageUrl(resolvedSrc);
    $[8] = props.src;
    $[9] = resolvedSrc;
  } else {
    resolvedSrc = $[9];
  }
  const t2 = props.fit ?? "cover";
  let t3;
  if ($[10] !== props.height || $[11] !== props.width || $[12] !== t2) {
    t3 = {
      width: props.width,
      height: props.height,
      objectFit: t2
    };
    $[10] = props.height;
    $[11] = props.width;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== props.alt || $[15] !== props.height || $[16] !== props.width || $[17] !== resolvedSrc || $[18] !== t3) {
    t4 = <img src={resolvedSrc} alt={props.alt} width={props.width} height={props.height} style={t3} />;
    $[14] = props.alt;
    $[15] = props.height;
    $[16] = props.width;
    $[17] = resolvedSrc;
    $[18] = t3;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== t4 || $[21] !== wrapperStyle) {
    t5 = <div style={wrapperStyle}>{t4}</div>;
    $[20] = t4;
    $[21] = wrapperStyle;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  return t5;
}